import { useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  useDeleteManualImage,
  useDevices,
  useDeviceTypes,
  useManual,
  useManualCategories,
  useProjectTemplates,
  useSaveManual,
  useSaveManualCategory,
  useUploadManualFile,
  useUploadManualImage
} from '../api/queries'
import type { ManualImage, ManualKind } from '../api/types'
import { ManualText } from '../components/ManualText'
import { Card, ErrorBox, Loading } from '../components/ui'
import { prepareDocumentFile } from '../lib/documentPhoto'
import { describeSize } from './ManualsPage'

/**
 * Skriv eller ret en manual (docs/DECISIONS.md beslutning 063).
 *
 * <b>Slagsen vælges én gang, ved oprettelsen</b>, og kan ikke ændres bagefter:
 * en skrevet manual og en uploadet fil er to forskellige ting, og et skifte
 * ville enten smide teksten væk eller efterlade en fil, ingen længere kan se.
 *
 * Editoren er et almindeligt tekstfelt med en værktøjslinje og en
 * forhåndsvisning ved siden af. Ikke et tekstbehandlingsprogram: den, der
 * skriver to manualer om året, skal kunne se, hvad der sker, uden at lære et
 * værktøj at kende.
 */
export function ManualEditorPage() {
  const { id } = useParams()
  const routeId = id ? Number(id) : undefined

  const navigate = useNavigate()

  /**
   * Manualen kan blive gemt undervejs - foerste gang man laegger et billede i en
   * ny manual (se pickImage). Derefter arbejder siden videre paa den gemte
   * manual UDEN at navigere, saa det man har skrevet ikke skal hentes forfra.
   */
  const [savedId, setSavedId] = useState<number | null>(null)
  const manualId = routeId ?? savedId ?? undefined

  // "Ny" handler om, hvordan siden blev aabnet: slagsen kan kun vaelges ved
  // oprettelsen, ogsaa efter at udkastet er blevet gemt.
  const isNew = routeId === undefined

  const { data, isLoading, error } = useManual(manualId ?? 0)
  const { data: categories } = useManualCategories()
  const { data: templates } = useProjectTemplates()
  const { data: devices } = useDevices({ includeInactive: true })

  const save = useSaveManual()
  const uploadFile = useUploadManualFile()
  const uploadImage = useUploadManualImage()
  const deleteImage = useDeleteManualImage()
  const saveCategory = useSaveManualCategory()

  const [form, setForm] = useState<{
    title: string
    summary: string
    kind: ManualKind
    categoryId: string
    content: string
    isActive: boolean
    deviceTypes: string[]
    templateIds: number[]
  } | null>(isNew ? emptyForm() : null)

  const [saveError, setSaveError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [typeDraft, setTypeDraft] = useState('')
  const [newShelf, setNewShelf] = useState<string | null>(null)

  const contentRef = useRef<HTMLTextAreaElement>(null)
  const filePicker = useRef<HTMLInputElement>(null)
  const imagePicker = useRef<HTMLInputElement>(null)

  // Enhedstyperne der allerede er i brug. De foreslaas, men feltet er frit:
  // en ny leverandoer skal kunne skrives ind uden at nogen foerst opretter en
  // enhed af den type.
  // Den faste liste (beslutning 105) kommer med, saa manualen staves som enhederne.
  const { data: deviceTypeList } = useDeviceTypes()
  const knownTypes = useMemo(() => {
    const types = new Set<string>()
    for (const type of deviceTypeList ?? []) types.add(type.name)
    for (const device of devices ?? []) {
      if (device.deviceType) types.add(device.deviceType)
    }
    return [...types].sort((a, b) => a.localeCompare(b, 'da'))
  }, [devices, deviceTypeList])

  if (!isNew && isLoading) return <Loading />
  if (!isNew && error) return <ErrorBox error={error} />

  // Foerste gang data er hentet, fyldes formularen.
  if (form === null) {
    if (!data) return <Loading />

    setForm({
      title: data.manual.title,
      summary: data.manual.summary ?? '',
      kind: data.manual.kind,
      categoryId: data.manual.categoryId ? String(data.manual.categoryId) : '',
      content: data.content ?? '',
      isActive: data.manual.isActive,
      deviceTypes: [...data.manual.deviceTypes],
      templateIds: data.manual.templates.map((t) => t.id)
    })

    return <Loading />
  }

  const images = data?.images ?? []

  function update<K extends keyof NonNullable<typeof form>>(
    key: K,
    value: NonNullable<typeof form>[K]
  ) {
    setForm((current) => (current ? { ...current, [key]: value } : current))
  }

  /**
   * Gemmer det, der staar i formularen, og svarer med manualens id.
   * Null betyder, at der ikke blev gemt - beskeden staar i saveError.
   */
  async function persistAsync(): Promise<number | null> {
    if (!form) return null

    if (!form.title.trim()) {
      setSaveError('Giv manualen en titel foerst - saa kan den gemmes.')
      return null
    }

    setSaveError(null)

    const saved = await save.mutateAsync({
      id: manualId,
      input: {
        title: form.title.trim(),
        summary: form.summary.trim() || null,
        kind: form.kind,
        categoryId: form.categoryId ? Number(form.categoryId) : null,
        content: form.kind === 'written' ? form.content : null,
        isActive: form.isActive,
        deviceTypes: form.deviceTypes,
        templateIds: form.templateIds
      }
    })

    // Fra nu af retter siden den gemte manual i stedet for at oprette en ny.
    setSavedId(saved.manual.id)
    return saved.manual.id
  }

  async function submit() {
    setBusy(true)
    try {
      const id = await persistAsync()
      if (id) navigate(`/manuals/${id}`)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Manualen kunne ikke gemmes.')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Saetter et stykke opmaerkning ind, hvor markoeren staar. Er der markeret
   * tekst, laegges den ind imellem - saa "**" paa markeret tekst goer den fed,
   * frem for at skrive to stjerner.
   */
  function insert(before: string, after = '', placeholder = '') {
    const field = contentRef.current
    if (!field || !form) return

    const start = field.selectionStart
    const end = field.selectionEnd
    const selected = form.content.slice(start, end) || placeholder

    const next = form.content.slice(0, start) + before + selected + after + form.content.slice(end)
    update('content', next)

    // Markoeren tilbage midt i det indsatte, saa man kan skrive videre.
    requestAnimationFrame(() => {
      field.focus()
      field.setSelectionRange(start + before.length, start + before.length + selected.length)
    })
  }

  /**
   * Manualens id - gemmer den først, hvis den aldrig har været gemt.
   *
   * **Både en fil og et billede skal have et manual-id at høre til**
   * (`POST /api/manuals/{id}/...`), og på en ny manual findes det ikke endnu.
   * Før var knapperne derfor grå, og forklaringen stod i en tooltip, ingen ser.
   * Nu gemmes udkastet, og man kommer videre: man skal ikke lære en rækkefølge
   * for at kunne skrive en manual (beslutning 063).
   */
  async function ensureSavedAsync(): Promise<number | null> {
    return manualId ?? (await persistAsync())
  }

  async function pickFile(file: File) {
    setBusy(true)
    try {
      const id = await ensureSavedAsync()
      if (!id) return

      await uploadFile.mutateAsync({ id, file })
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Filen kunne ikke lægges op.')
    } finally {
      setBusy(false)
    }
  }

  async function pickImage(file: File) {
    setBusy(true)
    try {
      const id = await ensureSavedAsync()
      if (!id) return

      // Samme skalering som sagernes dokumentationsfotos: hele motivet med, og
      // et typenummer skal stadig kunne laeses (beslutning 023).
      const prepared = await prepareDocumentFile(file)
      const image = await uploadImage.mutateAsync({ id, file: prepared.blob })

      insert(`\n![${file.name.replace(/\.[^.]+$/, '')}](billede:${image.id})\n`)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Billedet kunne ikke lægges op.')
    } finally {
      setBusy(false)
    }
  }

  async function removeImage(image: ManualImage) {
    if (!manualId) return
    if (!window.confirm(`Fjern billedet "${image.name}"? Henvisninger til det i teksten bliver tomme.`)) {
      return
    }

    await deleteImage.mutateAsync({ id: manualId, imageId: image.id })
  }

  function toggleTemplate(templateId: number) {
    if (!form) return

    update(
      'templateIds',
      form.templateIds.includes(templateId)
        ? form.templateIds.filter((t) => t !== templateId)
        : [...form.templateIds, templateId]
    )
  }

  /**
   * Opretter en hylde og saetter manualen paa den med det samme.
   *
   * <b>Den staar her og ikke kun paa en adminside</b>, fordi det er her, man
   * opdager, at hylden mangler: midt i at skrive manualen. At skulle et andet
   * sted hen og tilbage igen ville betyde, at folk bare lader den staa "Uden
   * hylde" (beslutning 067).
   */
  async function createShelf() {
    const name = (newShelf ?? '').trim()
    if (!name) return

    setBusy(true)
    setSaveError(null)
    try {
      // Bagerst i raekken - den, der opretter en hylde midt i noget andet, skal
      // ikke ogsaa tage stilling til, hvor den skal staa.
      const next = Math.max(0, ...(categories ?? []).map((c) => c.sortOrder)) + 1
      const created = await saveCategory.mutateAsync({
        input: { name, sortOrder: next, isActive: true }
      })

      update('categoryId', String(created.id))
      setNewShelf(null)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Hylden kunne ikke oprettes.')
    } finally {
      setBusy(false)
    }
  }

  function addDeviceType() {
    const type = typeDraft.trim()
    if (!form || !type) return

    if (!form.deviceTypes.some((d) => d.toLowerCase() === type.toLowerCase())) {
      update('deviceTypes', [...form.deviceTypes, type])
    }

    setTypeDraft('')
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          {isNew ? 'Ny manual' : 'Rediger manual'}
        </h1>
        <div className="btn-group">
          <Link className="btn" to={manualId ? `/manuals/${manualId}` : '/manuals'}>
            Fortryd
          </Link>
          <button className="btn btn--primary" onClick={submit} disabled={busy}>
            {busy ? 'Gemmer ...' : 'Gem'}
          </button>
        </div>
      </div>

      {saveError && <div className="error-box">{saveError}</div>}

      <Card title="Om manualen">
        <div className="stack" style={{ gap: 12 }}>
          <label className="stack" style={{ gap: 4 }}>
            <span className="tiny muted">Titel</span>
            <input
              value={form.title}
              onChange={(event) => update('title', event.target.value)}
              placeholder="Idriftsættelse af Easee-ladestander"
            />
          </label>

          <label className="stack" style={{ gap: 4 }}>
            <span className="tiny muted">Kort beskrivelse - hvad dækker den?</span>
            <input
              value={form.summary}
              onChange={(event) => update('summary', event.target.value)}
              placeholder="Fra opsætning til første ladning, inkl. tilmelding hos netselskabet"
            />
          </label>

          <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
            <label className="stack" style={{ gap: 4, minWidth: 180 }}>
              <span className="tiny muted">Hylde</span>
              {newShelf === null ? (
                <div className="row" style={{ gap: 6 }}>
                  <select
                    style={{ flex: 1 }}
                    value={form.categoryId}
                    onChange={(event) => update('categoryId', event.target.value)}
                  >
                    <option value="">Uden hylde</option>
                    {(categories ?? []).map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {/* Hylden mangler som regel foerst, naar man staar midt i at
                      skrive manualen - saa den kan laves her (beslutning 067). */}
                  <button
                    type="button"
                    className="btn btn--sm"
                    title="Opret en ny hylde"
                    onClick={() => setNewShelf('')}
                  >
                    + Ny
                  </button>
                </div>
              ) : (
                <div className="row" style={{ gap: 6 }}>
                  <input
                    autoFocus
                    style={{ flex: 1 }}
                    placeholder="Fx Nødbelysning"
                    value={newShelf}
                    onChange={(event) => setNewShelf(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                        void createShelf()
                      }
                      if (event.key === 'Escape') setNewShelf(null)
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn--sm btn--primary"
                    disabled={!newShelf.trim() || busy}
                    onClick={() => void createShelf()}
                  >
                    Opret
                  </button>
                  <button type="button" className="btn btn--sm" onClick={() => setNewShelf(null)}>
                    Fortryd
                  </button>
                </div>
              )}
            </label>

            <label className="stack" style={{ gap: 4, minWidth: 180 }}>
              <span className="tiny muted">Slags</span>
              {isNew ? (
                <select
                  value={form.kind}
                  onChange={(event) => update('kind', event.target.value as ManualKind)}
                >
                  <option value="written">Skriv den her</option>
                  <option value="file">Læg en færdig fil op</option>
                </select>
              ) : (
                <div className="small" style={{ paddingTop: 6 }}>
                  {form.kind === 'file' ? 'Uploadet fil' : 'Skrevet her'}
                  <div className="tiny muted">Kan ikke ændres - opret en ny i stedet</div>
                </div>
              )}
            </label>

            <label className="row" style={{ gap: 6, alignItems: 'center', paddingTop: 18 }}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) => update('isActive', event.target.checked)}
              />
              <span className="small">Aktiv</span>
            </label>
          </div>
        </div>
      </Card>

      <Card title="Hvor hører den til?">
        <p className="small muted" style={{ marginTop: 0 }}>
          Hænger manualen på en enhedstype, ligger den klar, når mærkatet scannes. Hænger den på en
          procedureskabelon, følger den med den slags sager.
        </p>

        <div className="stack" style={{ gap: 6 }}>
          <span className="tiny muted">Enhedstyper</span>

          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {form.deviceTypes.map((type) => (
              <span key={type} className="pill" style={{ background: '#E0F2FE', color: '#075985' }}>
                {type}
                <button
                  className="pill__remove"
                  onClick={() =>
                    update(
                      'deviceTypes',
                      form!.deviceTypes.filter((d) => d !== type)
                    )
                  }
                  aria-label={`Fjern ${type}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          <div className="row" style={{ gap: 6 }}>
            <input
              list="manual-device-types"
              style={{ maxWidth: 260 }}
              placeholder="Fx Ladestander"
              value={typeDraft}
              onChange={(event) => setTypeDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  addDeviceType()
                }
              }}
            />
            <datalist id="manual-device-types">
              {knownTypes.map((type) => (
                <option key={type} value={type} />
              ))}
            </datalist>
            <button className="btn btn--sm" onClick={addDeviceType} disabled={!typeDraft.trim()}>
              Tilføj
            </button>
          </div>
        </div>

        <div className="stack" style={{ gap: 6, marginTop: 14 }}>
          <span className="tiny muted">Sagstyper (procedureskabeloner)</span>
          <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
            {(templates ?? []).map((template) => (
              <label key={template.id} className="row small" style={{ gap: 6, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={form!.templateIds.includes(template.id)}
                  onChange={() => toggleTemplate(template.id)}
                />
                {template.name}
              </label>
            ))}
            {(templates ?? []).length === 0 && (
              <span className="tiny muted">Der er ingen procedureskabeloner endnu.</span>
            )}
          </div>
        </div>
      </Card>

      {form.kind === 'file' ? (
        <Card title="Dokumentet">
          <input
            ref={filePicker}
            type="file"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = ''
              if (file) void pickFile(file)
            }}
          />

          {data?.manual.fileName ? (
            <div className="small" style={{ marginBottom: 10 }}>
              {data.manual.fileName} · {describeSize(data.manual.sizeBytes)}
              <div className="tiny muted">
                Lægger du en ny fil op, afløser den denne. Den gamle bliver liggende i SharePoint.
              </div>
            </div>
          ) : (
            <p className="small muted" style={{ marginTop: 0 }}>
              {manualId
                ? 'Der er ikke lagt en fil op endnu.'
                : 'Vælg dokumentet - manualen bliver gemt samtidig.'}
            </p>
          )}

          {/* Samme som billedknappen: udkastet gemmes, naar filen vaelges, saa
              man ikke skal laere en raekkefoelge. Se ensureSavedAsync. */}
          <button
            className="btn btn--primary"
            disabled={busy}
            onClick={() => filePicker.current?.click()}
          >
            {data?.manual.fileName ? 'Læg en ny fil op' : 'Vælg dokument'}
          </button>
        </Card>
      ) : (
        <Card title="Teksten">
          <input
            ref={imagePicker}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = ''
              if (file) void pickImage(file)
            }}
          />

          <div className="btn-group" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
            <button className="btn btn--sm" onClick={() => insert('## ', '', 'Overskrift')}>
              Overskrift
            </button>
            <button className="btn btn--sm" onClick={() => insert('**', '**', 'fed tekst')}>
              Fed
            </button>
            <button className="btn btn--sm" onClick={() => insert('*', '*', 'kursiv')}>
              Kursiv
            </button>
            <button className="btn btn--sm" onClick={() => insert('\n- ', '', 'punkt')}>
              Punkt
            </button>
            <button className="btn btn--sm" onClick={() => insert('\n1. ', '', 'trin')}>
              Nummereret
            </button>
            <button className="btn btn--sm" onClick={() => insert('\n> ', '', 'husk det her')}>
              Fremhævet
            </button>
            {/* Ikke laengere graa paa en ny manual: udkastet gemmes, naar der
                vaelges et billede. Se ensureSavedAsync. */}
            <button
              className="btn btn--sm"
              disabled={busy}
              onClick={() => imagePicker.current?.click()}
              title={manualId ? undefined : 'Manualen bliver gemt, når du vælger billedet'}
            >
              Indsæt billede
            </button>
          </div>

          <div className="manual-editor">
            <textarea
              ref={contentRef}
              className="manual-editor__input"
              value={form.content}
              onChange={(event) => update('content', event.target.value)}
              placeholder={
                '## Før du kører ud\n' +
                '- Tjek at anlægget er anmeldt\n' +
                '- Tag måleinstrumentet med\n\n' +
                '## På stedet\n' +
                '1. Afbryd og kontrollér spændingsløs tilstand\n' +
                '2. ...\n\n' +
                '> Husk at fotografere tavlen, før du lukker den'
              }
            />

            <div className="manual-editor__preview">
              <div className="tiny muted" style={{ marginBottom: 6 }}>
                Sådan kommer den til at se ud
              </div>
              {form.content.trim() ? (
                <ManualText content={form.content} manualId={manualId ?? 0} images={images} />
              ) : (
                <div className="tiny muted">Skriv noget i feltet til venstre.</div>
              )}
            </div>
          </div>

          {images.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div className="tiny muted" style={{ marginBottom: 6 }}>
                Billeder i manualen. Sæt et ind i teksten med knappen ovenfor.
              </div>
              <div className="stack" style={{ gap: 4 }}>
                {images.map((image) => (
                  <div key={image.id} className="row row--between small" style={{ gap: 8 }}>
                    <span style={{ minWidth: 0 }}>
                      <code>billede:{image.id}</code> · {image.name}
                      <span className="tiny muted">
                        {' '}
                        · {image.uploadedBy?.displayName ?? 'ukendt'}
                      </span>
                    </span>
                    <span className="btn-group">
                      <button className="btn btn--sm" onClick={() => insert(`\n![](billede:${image.id})\n`)}>
                        Sæt ind
                      </button>
                      <button className="btn btn--sm" onClick={() => removeImage(image)}>
                        Fjern
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

function emptyForm() {
  return {
    title: '',
    summary: '',
    kind: 'written' as ManualKind,
    categoryId: '',
    content: '',
    isActive: true,
    deviceTypes: [] as string[],
    templateIds: [] as number[]
  }
}
